import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Lock as LockIcon,
  AccountCircle as AccountCircleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import DashboardHeader from '../common/DashboardHeader';
import ProfileFaceLoginSection from './ProfileFaceLoginSection';
import ChangePasswordDialog from './ChangePasswordDialog';
import useMobileView from '../../hooks/useMobileView';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import { getUserProfile } from '../../services/api';
import {
  DASHBOARD_ACCENT,
  getDashboardPageShellSx,
  getProfileActiveChipSx,
  getProfileAvatarSx,
  getProfileFieldLabelSx,
  getProfileFieldValueSx,
  getProfileHeroCardSx,
  getProfileMetaPillSx,
  getProfilePanelCardSx,
  getProfilePanelHeaderSx,
  getProfilePrimaryButtonSx,
  getProfileSectionIconSx,
  getProfileSurfaceTokens,
} from '../../utils/profilePageLayout';
import moment from 'moment';

function ProfileInfoRow({ label, value, icon: Icon, isDark }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        border: `1px solid ${getProfileSurfaceTokens(isDark).surfaceBorder}`,
        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
      }}
    >
      <Typography sx={getProfileFieldLabelSx(isDark)}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {Icon ? <Icon sx={{ fontSize: 18, color: DASHBOARD_ACCENT, flexShrink: 0 }} /> : null}
        <Typography sx={{ ...getProfileFieldValueSx(isDark), wordBreak: 'break-word' }}>{value || '—'}</Typography>
      </Box>
    </Box>
  );
}

const ProfilePageContent = ({
  headerTitle = 'My Profile',
  breadcrumbs = [
    { label: 'Home', path: '/onboarding/landing/' },
    { label: 'Profile' },
  ],
  navLinks = [],
  forceDarkHeader = false,
  showFaceLogin = false,
  successMessageDefault = 'Profile updated successfully!',
}) => {
  const theme = useTheme();
  const { isCompact, layout } = useMobileView();
  const isDark = theme.palette.mode === 'dark';
  const useMobileDark = isCompact || isMobileShellLayout();
  const tokens = getProfileSurfaceTokens(isDark || useMobileDark);
  const pageShellSx = getDashboardPageShellSx(isDark ? 'dark' : 'light');
  const roleNames = useMemo(() => {
    const context = window.DJANGO_CONTEXT || {};
    return Array.isArray(context.user_groups)
      ? context.user_groups.map((groupName) => String(groupName || '').trim()).filter(Boolean)
      : [];
  }, []);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState(successMessageDefault);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserProfile();
      setProfile(data);
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChangeSuccess = async () => {
    try {
      setProfile(await getUserProfile());
    } catch {
      // Profile refresh is best-effort after password update.
    }
    setSuccessMessage('Password updated successfully!');
    setSuccess(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return moment(dateString).format('DD MMM YYYY, hh:mm A');
  };

  const getInitials = () => {
    const firstName = profile?.first_name || '';
    const lastName = profile?.last_name || '';
    if (firstName || lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    return profile?.username?.charAt(0).toUpperCase() || 'U';
  };

  const displayName = profile?.first_name || profile?.last_name
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
    : profile?.username;

  const shellSx = useMobileDark
    ? { px: layout.pagePaddingX, pt: 0, pb: layout.pagePaddingY, backgroundColor: 'transparent', minHeight: 'auto' }
    : { ...pageShellSx, minHeight: '100vh' };

  if (loading) {
    return (
      <Box sx={{ ...shellSx, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: useMobileDark ? '40vh' : '100vh' }}>
        <CircularProgress sx={{ color: DASHBOARD_ACCENT }} />
      </Box>
    );
  }

  if (error && !profile) {
    return (
      <Box sx={shellSx}>
        <DashboardHeader title={headerTitle} forceDark={forceDarkHeader || useMobileDark} breadcrumbs={breadcrumbs} navLinks={navLinks} />
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      <Box sx={{ mb: useMobileDark ? 0.75 : 2 }}>
        <DashboardHeader
          title={headerTitle}
          forceDark={forceDarkHeader || useMobileDark}
          breadcrumbs={breadcrumbs}
          navLinks={navLinks}
        />
      </Box>

      <Grid container spacing={useMobileDark ? 1.5 : 2.5}>
        <Grid item xs={12}>
          <Card elevation={0} sx={getProfileHeroCardSx(isDark || useMobileDark, useMobileDark)}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                  gap: { xs: 2, md: 2.5 },
                }}
              >
                <Avatar sx={getProfileAvatarSx(isDark || useMobileDark)}>{getInitials()}</Avatar>

                <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.75rem' }, color: tokens.text, lineHeight: 1.2 }}>
                      {displayName}
                    </Typography>
                    {profile?.is_active ? <Chip label="Active" size="small" sx={getProfileActiveChipSx()} /> : null}
                  </Box>
                  <Typography sx={{ color: tokens.subtleText, fontSize: '0.92rem', mb: 0.35 }}>
                    {profile?.email || '—'}
                  </Typography>
                  <Typography sx={{ color: tokens.subtleText, fontSize: '0.82rem', mb: 1.5 }}>
                    @{profile?.username}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    {profile?.hotels?.length > 0 ? (
                      <Box sx={getProfileMetaPillSx(isDark || useMobileDark)}>
                        <BusinessIcon sx={{ fontSize: 16, color: DASHBOARD_ACCENT }} />
                        {profile.hotels.map((h) => h.name).join(', ')}
                      </Box>
                    ) : null}
                    {profile?.organisation ? (
                      <Box sx={getProfileMetaPillSx(isDark || useMobileDark)}>
                        <LocationIcon sx={{ fontSize: 16, color: DASHBOARD_ACCENT }} />
                        {profile.organisation.name}
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    variant="contained"
                    fullWidth={isCompact}
                    startIcon={<LockIcon />}
                    onClick={() => setPasswordDialogOpen(true)}
                    sx={getProfilePrimaryButtonSx()}
                  >
                    Change Password
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} order={{ xs: 2, md: 3 }}>
          <Card elevation={0} sx={getProfilePanelCardSx(isDark || useMobileDark, useMobileDark)}>
            <Box sx={getProfilePanelHeaderSx(isDark || useMobileDark)}>
              <Box sx={getProfileSectionIconSx(isDark || useMobileDark)}>
                <CalendarIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: tokens.text, fontSize: '1rem' }}>Account Information</Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ProfileInfoRow label="Member Since" value={formatDate(profile?.date_joined)} isDark={isDark || useMobileDark} />
              {profile?.last_login ? (
                <ProfileInfoRow label="Last Login" value={formatDate(profile.last_login)} isDark={isDark || useMobileDark} />
              ) : null}
              {profile?.hotels?.map((hotel, index) => (
                <ProfileInfoRow
                  key={hotel.id || index}
                  label={index === 0 ? 'Hotels' : 'Hotel'}
                  value={[hotel.name, [hotel.city, hotel.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                  isDark={isDark || useMobileDark}
                />
              ))}
              {profile?.organisation ? (
                <ProfileInfoRow label="Organisation" value={profile.organisation.name} isDark={isDark || useMobileDark} />
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} order={{ xs: 3, md: 2 }}>
          <Card elevation={0} sx={getProfilePanelCardSx(isDark || useMobileDark, useMobileDark)}>
            <Box sx={getProfilePanelHeaderSx(isDark || useMobileDark)}>
              <Box sx={getProfileSectionIconSx(isDark || useMobileDark)}>
                <AccountCircleIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: tokens.text, fontSize: '1rem' }}>Personal Information</Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <ProfileInfoRow label="First Name" value={profile?.first_name} isDark={isDark || useMobileDark} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ProfileInfoRow label="Last Name" value={profile?.last_name} isDark={isDark || useMobileDark} />
                </Grid>
                <Grid item xs={12}>
                  <ProfileInfoRow label="Email" value={profile?.email} icon={EmailIcon} isDark={isDark || useMobileDark} />
                </Grid>
                <Grid item xs={12}>
                  <ProfileInfoRow label="Username" value={profile?.username} isDark={isDark || useMobileDark} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ ...getProfilePanelCardSx(isDark || useMobileDark, useMobileDark), mt: 2.5 }}>
            <Box sx={getProfilePanelHeaderSx(isDark || useMobileDark)}>
              <Box sx={getProfileSectionIconSx(isDark || useMobileDark)}>
                <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: tokens.text, fontSize: '1rem' }}>Role & Permissions</Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={getProfileFieldLabelSx(isDark || useMobileDark)}>Assigned Group</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                  {roleNames.length > 0 ? roleNames.map((roleName) => (
                    <Chip
                      key={roleName}
                      label={roleName}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        color: isDark || useMobileDark ? '#dffcf5' : '#0f766e',
                        backgroundColor: isDark || useMobileDark ? 'rgba(76,225,182,0.16)' : 'rgba(76,225,182,0.12)',
                        border: `1px solid ${isDark || useMobileDark ? 'rgba(76,225,182,0.28)' : 'rgba(13,148,136,0.2)'}`,
                      }}
                    />
                  )) : (
                    <Typography sx={getProfileFieldValueSx(isDark || useMobileDark)}>No group assigned</Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {showFaceLogin ? (
            <ProfileFaceLoginSection
              isDark={isDark || useMobileDark}
              cardSx={getProfilePanelCardSx(isDark || useMobileDark, useMobileDark)}
              onNotify={(message) => {
                setSuccessMessage(message);
                setSuccess(true);
              }}
            />
          ) : null}
        </Grid>
      </Grid>

      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
        forceDark={useMobileDark}
      />

      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: isCompact ? 'center' : 'right' }}>
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: isCompact ? 'center' : 'right' }}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePageContent;
